/** iPad / 旧 Safari 白屏定位：URL ?compat=0–7 或 localStorage moji_compat_level */

import {
  safeStorageGetItem,
  safeStorageRemoveItem,
  safeStorageSetItem,
} from '@/utils/safeLocalStorage'

export const COMPAT_STORAGE_KEY = 'moji_compat_level'
export const COMPAT_MAX_LEVEL = 7

export interface CompatLevelInfo {
  level: number
  name: string
  desc: string
  enabled: string[]
}

export const COMPAT_LEVELS: CompatLevelInfo[] = [
  {
    level: 0,
    name: '纯 HTML',
    desc: '服务端直出纯 HTML，零 Vue 客户端',
    enabled: ['HTML 直出', '无 Nuxt 客户端 JS'],
  },
  {
    level: 1,
    name: 'Vue 空壳',
    desc: 'Vue 挂载 + empty 布局',
    enabled: ['Vue 3', 'vue-i18n'],
  },
  {
    level: 2,
    name: 'Header + Footer',
    desc: 'Element Plus 顶栏/底栏',
    enabled: ['Element Plus', '导航组件'],
  },
  {
    level: 3,
    name: '首页骨架',
    desc: '首页布局 + 占位天气，不请求 API',
    enabled: ['home.scss', 'Hero 骨架', '无天气 API'],
  },
  {
    level: 4,
    name: '+ 天气 API',
    desc: '拉取城市天气与详情区',
    enabled: ['天气接口', '天气详情 6 卡'],
  },
  {
    level: 5,
    name: '+ 生活指数',
    desc: '生活服务网格',
    enabled: ['生活指数区块'],
  },
  {
    level: 6,
    name: '+ ECharts',
    desc: '逐小时 / 15 天图表',
    enabled: ['echarts', 'HourChart', 'DayChart'],
  },
  {
    level: 7,
    name: '完整版',
    desc: '雷达 iframe、资讯、埋点等全部功能',
    enabled: ['雷达', '热门城市', '资讯', '埋点'],
  },
]

export function parseCompatLevel(input: string | null | undefined): number | null {
  if (input == null || input === '') return null
  if (input === 'basic') return 3
  const n = Number.parseInt(input, 10)
  if (Number.isNaN(n) || n < 0 || n > COMPAT_MAX_LEVEL) return null
  return n
}

export function readCompatLevelFromSearch(search: string): number | null {
  if (!search) return null
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search)
  return parseCompatLevel(params.get('compat'))
}

/** query 优先，其次 localStorage；均无则 null（正常完整模式） */
export function resolveCompatLevel(): number | null {
  if (typeof window !== 'undefined') {
    const fromUrl = readCompatLevelFromSearch(window.location.search)
    if (fromUrl != null) {
      safeStorageSetItem(COMPAT_STORAGE_KEY, String(fromUrl))
      return fromUrl
    }
    const stored = safeStorageGetItem(COMPAT_STORAGE_KEY)
    const parsed = parseCompatLevel(stored)
    if (parsed != null) return parsed
    return null
  }

  return null
}

export function clearCompatLevel() {
  if (typeof window === 'undefined') return
  safeStorageRemoveItem(COMPAT_STORAGE_KEY)
}

export function compatLevelUrl(level: number | null, basePath?: string): string {
  const path = basePath ?? (typeof window !== 'undefined' ? window.location.pathname : '/zh-CN')
  if (level == null) {
    const url = new URL(path, 'http://local')
    url.searchParams.delete('compat')
    return `${url.pathname}${url.search}`
  }
  if (level <= 2) {
    const prefix = path.includes('/en-US') ? '/en-US' : '/zh-CN'
    return `${prefix}/compat?compat=${level}`
  }
  const url = new URL(path, 'http://local')
  url.searchParams.set('compat', String(level))
  return `${url.pathname}?${url.searchParams.toString()}`
}

export interface CompatFeatures {
  isActive: boolean
  level: number
  bury: boolean
  weatherApi: boolean
  forceHomeShell: boolean
  lifeIndex: boolean
  charts: boolean
  detailSection: boolean
  extras: boolean
  radarIframe: boolean
}

/** level=null 表示正常完整模式 */
export function getCompatFeatures(level: number | null): CompatFeatures {
  const lv = level ?? COMPAT_MAX_LEVEL
  const active = level !== null

  return {
    isActive: active,
    level: lv,
    bury: !active || lv >= COMPAT_MAX_LEVEL,
    weatherApi: !active || lv >= 4,
    forceHomeShell: active && lv === 3,
    lifeIndex: !active || lv >= 5,
    charts: !active || lv >= 6,
    detailSection: !active || lv >= 4,
    extras: !active || lv >= COMPAT_MAX_LEVEL,
    radarIframe: !active || lv >= COMPAT_MAX_LEVEL,
  }
}
